'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { IconAdjustments } from '@tabler/icons-react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import qs from 'qs'
import { modifyPath } from 'ramda'

import {
  Brand,
  FilterTypes,
  FilterValues,
  useFilterChange
} from 'modules/products'
import Button from 'shared/ui/Button'
import { Drawer, DrawerTitle } from 'shared/ui/Drawer'
import { searchParamsToParsedUrlQuery } from 'shared/utils/common'
import { isNotNilOrEmpty } from 'shared/utils/ramda'
import { addTestId } from 'shared/utils/tests'

import { FilterDictionary } from '../types'
import { clearQueryParams, haveFiltersChanged } from '../utils'

import PriceFilterMobile from './PriceFilterMobile'
import ProducerFilterMobile from './ProducerFilterMobile'
import TagsFilterMobile from './TagsFilterMobile'
import WeeklyAvailabilityFilterMobile from './WeeklyAvailabilityFilterMobile'

import styles from './MobileFiltersModal.module.scss'

interface MobileFiltersModalProps {
  brands: Brand[]
  tags: FilterDictionary[]
  deliveryDates: string[]
}

const defaultValues = {
  price: null,
  brand_id: null,
  country_of_origin: null,
  daily_availability_plan: null,
  show_unavailable_products: null,
  favourite_products: null,
  tag: null,
  multipack: null
}

const MobileFiltersModal = ({
  brands,
  tags,
  deliveryDates
}: MobileFiltersModalProps) => {
  const [open, setOpen] = useState(false)
  const [values, setValues] = useState<FilterTypes>(defaultValues)
  const [cachedValues, setCachedValues] = useState<FilterTypes>(defaultValues)

  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { setLastFilterChange } = useFilterChange()

  const queryFilters: FilterValues = useMemo(() => {
    const parsedQuery = searchParamsToParsedUrlQuery(searchParams)

    return (parsedQuery?.filter as FilterValues) || {}
  }, [searchParams])
  const hasFilters = useMemo(
    () => isNotNilOrEmpty(queryFilters),
    [queryFilters]
  )
  const isUpdateDisabled = useMemo(
    () => !haveFiltersChanged(values, queryFilters),
    [queryFilters, values]
  )

  const activeFiltersCount = useMemo(() => {
    return Object.values(queryFilters).filter(el => isNotNilOrEmpty(el)).length
  }, [queryFilters])

  const handleOpen = useCallback(() => setOpen(true), [setOpen])
  const handleClose = useCallback(() => setOpen(false), [setOpen])

  const handleValueUpdate = useCallback(
    (path: string, value: string | string[] | boolean | null) => {
      const pathSegments = path.split('.')

      setValues(prev => modifyPath(pathSegments, () => value, prev))
    },
    [setValues]
  )

  const handleClickClose = useCallback(() => {
    setLastFilterChange(null)
    handleClose()
    setValues(cachedValues)
  }, [cachedValues, handleClose, setValues, setLastFilterChange])

  const handleClearFilters = useCallback(() => {
    setLastFilterChange(null)
    handleClose()
    setValues(defaultValues)

    router.replace(pathname || '')
  }, [pathname, router, handleClose, setLastFilterChange])

  const handleSubmitFilters = async () => {
    const parsedQuery = searchParamsToParsedUrlQuery(searchParams)
    const filtersToSet: FilterValues = Object.fromEntries(
      Object.entries(values).filter(([_, val]) => isNotNilOrEmpty(val))
    )
    const params = qs.stringify(
      {
        ...clearQueryParams(parsedQuery),
        filter: filtersToSet
      },
      { addQueryPrefix: true }
    )

    setLastFilterChange(new Date())
    router.replace(`${pathname || ''}${decodeURIComponent(params)}`)

    handleClose()
  }

  useEffect(() => {
    if (open) {
      setCachedValues(values)
    }
  }, [open, values])

  useEffect(() => {
    setValues(prev => ({ ...prev, ...queryFilters }))
  }, [queryFilters])

  return (
    <>
      <div className={styles.triggerWrapper}>
        {activeFiltersCount > 0 && (
          <div
            className={styles.counter}
            {...addTestId('mobile-active-filters-counter')}
          >
            {activeFiltersCount}
          </div>
        )}
        <Button
          variant='neutralOutline'
          startIcon={<IconAdjustments />}
          onClick={handleOpen}
          fullWidth
          size='small'
        >
          Filtry
        </Button>
      </div>
      <Drawer
        id='mobile-filters-modal'
        placement='left'
        open={open}
        onClose={handleClickClose}
      >
        <div className={styles.drawerInner}>
          <div className={styles.filtersSection}>
            <DrawerTitle>Dostosuj filtry</DrawerTitle>
            <TagsFilterMobile
              tags={tags}
              onChange={handleValueUpdate}
              value={values.tag}
            />
            <PriceFilterMobile
              onChange={handleValueUpdate}
              value={values.price}
            />
            <ProducerFilterMobile
              brands={brands}
              onChange={handleValueUpdate}
              value={values.brand_id}
            />
            <WeeklyAvailabilityFilterMobile
              onChange={handleValueUpdate}
              value={values.daily_availability_plan}
              deliveryDates={deliveryDates}
            />
          </div>

          <div className={styles.buttonsWrapper}>
            <Button
              variant='primary'
              disabled={isUpdateDisabled}
              onClick={handleSubmitFilters}
              fullWidth
            >
              Zastosuj
            </Button>
            <Button
              variant='neutralOutline'
              onClick={hasFilters ? handleClearFilters : handleClickClose}
              fullWidth
            >
              Wyczyść
            </Button>
          </div>
        </div>
      </Drawer>
    </>
  )
}

export default MobileFiltersModal
